// Images the user sends, made ready in the browser before they go to the server (chat.ts uploads them):
// a photo larger than `FULL` pixels on a side is made smaller, and every photo is coded again (WebP, or
// JPEG where the browser does not code WebP) — which leaves out what the camera wrote into it, the place
// where it was taken among it, and turns it as the camera held it. A screenshot (PNG) small enough goes
// as it is, every pixel of its text sharp; an animation (GIF, WebP) as it is, moving. The chat shows a copy
// of `PREVIEW` pixels at most, made here too, and the image itself when one opens it. A user's picture is
// the middle square of an image, `AVATAR` pixels.
/** The most bytes of an image the server takes (protocol.MOST_FILE). */
export const MOST_FILE = 10 * 1024 * 1024;

/** The largest side of an image sent, of the copy the chat shows, and of a picture, px. */
const FULL = 2560;
const PREVIEW = 800;
const AVATAR = 256;
/** A PNG this small, and no larger than `FULL`, goes as it is. */
const KEEP_PNG = 2 * 1024 * 1024;
const QUALITY = 0.86;

/** An image to upload: its bytes, format (`png`, `jpg`, `gif`, `webp`) and pixels. */
export type Coded = { blob: Blob; kind: string; width: number; height: number };

/** An image made ready: itself, and the copy the chat shows (`null`: the image itself). */
export type Ready = { image: Coded; preview: Coded | null; name: string };

/** Why an image cannot go, in the user's words. */
export class NotAnImage extends Error {}

/** File `file`, an image the user picked, pasted or dropped, made ready to go. */
export async function prepare(file: File): Promise<Ready> {
  const kind = await kindOf(file);
  if (kind === null) throw new NotAnImage("это не изображение: можно PNG, JPEG, GIF и WebP");
  const name = file.name || `image.${kind}`;
  const bitmap = await decode(file);
  try {
    const { width, height } = bitmap;
    if (kind === "gif" || (kind === "webp" && (await animated(file)))) {
      if (file.size > MOST_FILE) throw new NotAnImage("анимация больше 10 МБ");
      return { image: { blob: file, kind, width, height }, preview: null, name };
    }
    const fits = Math.max(width, height) <= FULL;
    const image =
      kind === "png" && fits && file.size <= KEEP_PNG
        ? { blob: file as Blob, kind, width, height }
        : await coded(bitmap, scaleTo(width, height, FULL), hasAlpha(kind));
    if (image.blob.size > MOST_FILE) throw new NotAnImage("изображение больше 10 МБ");
    const preview =
      Math.max(width, height) > PREVIEW ? await coded(bitmap, scaleTo(width, height, PREVIEW), hasAlpha(kind)) : null;
    return { image, preview, name };
  } finally {
    bitmap.close();
  }
}

/** File `file` made a user's picture: its middle square, `AVATAR` pixels. */
export async function pictureOf(file: File): Promise<Coded> {
  if ((await kindOf(file)) === null) throw new NotAnImage("это не изображение: можно PNG, JPEG, GIF и WebP");
  const bitmap = await decode(file);
  try {
    const side = Math.min(bitmap.width, bitmap.height);
    const canvas = document.createElement("canvas");
    canvas.width = AVATAR;
    canvas.height = AVATAR;
    const g = canvas.getContext("2d")!;
    g.imageSmoothingQuality = "high";
    g.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, AVATAR, AVATAR);
    return await encoded(canvas, false);
  } finally {
    bitmap.close();
  }
}

/** The format of `file` by its first bytes; `null` if it is no image the server takes. */
async function kindOf(file: Blob): Promise<string | null> {
  const b = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const at = (bytes: number[], from = 0) => bytes.every((x, i) => b[from + i] === x);
  if (at([137, 80, 78, 71, 13, 10, 26, 10])) return "png";
  if (at([255, 216, 255])) return "jpg";
  if (at([71, 73, 70, 56])) return "gif";
  if (at([82, 73, 70, 70]) && at([87, 69, 66, 80], 8)) return "webp";
  return null;
}

/** Whether WebP file `file` moves: it has an animation's chunk. */
async function animated(file: Blob): Promise<boolean> {
  const head = new Uint8Array(await file.slice(0, 64).arrayBuffer());
  const text = String.fromCharCode(...head);
  return text.includes("ANIM") || ((head[20] ?? 0) & 2) !== 0 && text.includes("VP8X");
}

function hasAlpha(kind: string): boolean {
  return kind === "png" || kind === "webp";
}

async function decode(file: Blob): Promise<ImageBitmap> {
  try {
    // Turned as the camera held it (EXIF's orientation), which coding again leaves out.
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new NotAnImage("не получилось прочитать изображение");
  }
}

/** The size of an image of `width` × `height` made to fit `most` pixels on its larger side. */
function scaleTo(width: number, height: number, most: number): [number, number] {
  const k = Math.min(1, most / Math.max(width, height));
  return [Math.max(1, Math.round(width * k)), Math.max(1, Math.round(height * k))];
}

/** `bitmap` drawn at `size` and coded. */
async function coded(bitmap: ImageBitmap, size: [number, number], alpha: boolean): Promise<Coded> {
  const canvas = document.createElement("canvas");
  [canvas.width, canvas.height] = size;
  const g = canvas.getContext("2d")!;
  g.imageSmoothingQuality = "high";
  g.drawImage(bitmap, 0, 0, size[0], size[1]);
  return encoded(canvas, alpha);
}

/** What `canvas` holds, coded: WebP — or, where the browser does not code it, JPEG (PNG if the image
 * has transparent pixels). */
async function encoded(canvas: HTMLCanvasElement, alpha: boolean): Promise<Coded> {
  const webp = await blobOf(canvas, "image/webp");
  if (webp !== null && webp.type === "image/webp")
    return { blob: webp, kind: "webp", width: canvas.width, height: canvas.height };
  const type = alpha ? "image/png" : "image/jpeg";
  const other = await blobOf(canvas, type);
  if (other === null) throw new NotAnImage("не получилось подготовить изображение");
  return { blob: other, kind: alpha ? "png" : "jpg", width: canvas.width, height: canvas.height };
}

function blobOf(canvas: HTMLCanvasElement, type: string): Promise<Blob | null> {
  return new Promise((done) => canvas.toBlob(done, type, QUALITY));
}
